import React, { useContext } from 'react';

import { LocationContext } from 'app/containers/LocationProvider';
import css from './deliveryZoneTerms.module.scss';

type TLocationKey =
    | 'MSK' | 'SPB' | 'NIZ' | 'KZN' | 'TUL'
    | 'YAR' | 'VLA' | 'VOR' | 'RYA' | 'KLU' | 'TVE';

type TTermsSection = {
    title: string;
    rows: React.ReactNode[];
};

const SUPPORT_LINK = (
    <a href="https://elmntr.ee/sup" className={css.deliveryZoneTermsSupportLink}>
        через службу поддержки
    </a>
);

const isHeaderRow = (row: React.ReactNode): boolean =>
    typeof row === 'string'
    && (row.endsWith(':') || row.startsWith('Заказ необходимо оплатить'));

const DELIVERY_TITLE = 'Условия доставки';
const ADJUSTMENT_TITLE = 'Условия корректировки заказа';
const PAYMENT_TITLE = 'Оплата заказа';

const ORDER_VIA_SITE_ROW = 'Вы оформляете заказ на сайте и выбираете доступный интервал доставки';
const DELIVERY_BY_CITY_ROW = 'Доставка осуществляется нашими курьерами по городу (введите адрес выше, '
    + 'чтобы проверить возможность доставки)';
const NOTIFY_ROW = 'Мы присылаем уведомление о доставке за день до заказа и за 5 минут до прибытия курьера';

const MSK_DELIVERY_ROWS = [
    ORDER_VIA_SITE_ROW,
    'Доставка осуществляется нашими курьерами по Москве и МО до 60 км от МКАД',
    NOTIFY_ROW,
];
const SPB_DELIVERY_ROWS = [
    ORDER_VIA_SITE_ROW,
    'Доставка осуществляется нашими курьерами по Санкт-Петербургу и ЛО до 50 км от КАД',
    NOTIFY_ROW,
];
const CITY_DELIVERY_ROWS = [
    ORDER_VIA_SITE_ROW,
    DELIVERY_BY_CITY_ROW,
    NOTIFY_ROW,
];
const VOR_DELIVERY_ROWS = [
    ORDER_VIA_SITE_ROW,
    DELIVERY_BY_CITY_ROW,
    'Дни доставок: пятница, суббота, воскресенье',
    NOTIFY_ROW,
];
const MSK_LIKE_DELIVERY_ROWS = [
    ORDER_VIA_SITE_ROW,
    DELIVERY_BY_CITY_ROW,
    'День доставки: воскресенье',
    NOTIFY_ROW,
];

const ADJUSTMENT_MSK_LIKE_ROWS = [
    'Изменить дату доставки и состав заказа можно:',
    'самостоятельно — за 3 дня до доставки до 16:00',
    <>{SUPPORT_LINK} — за 2 дня до доставки до 16:00</>,
    'Изменить время и адрес доставки можно:',
    <>самостоятельно или {SUPPORT_LINK} — за 1 день до доставки до 16:00.</>,
];
const ADJUSTMENT_SPB_LIKE_ROWS = [
    'Изменить дату доставки и состав заказа можно:',
    'самостоятельно — за 4 дня до доставки до 16:00',
    <>{SUPPORT_LINK} — за 3 дня до доставки до 16:00</>,
    'Изменить время и адрес доставки можно:',
    <>самостоятельно или {SUPPORT_LINK} — за 1 день до доставки до 16:00.</>,
];

const PAYMENT_MSK_LIKE_ROWS = [
    'При оформлении заказа необходимо выбрать один из двух вариантов оплаты:',
    'Онлайн банковской картой — сразу, для заказов с подпиской и разовых',
    'СБП — при получении, только для разовых заказов',
    'Заказ необходимо оплатить до 16.00 по московскому времени за 3 дня до доставки.',
];
const PAYMENT_SPB_LIKE_ROWS = [
    'При оформлении заказа необходимо выбрать один из двух вариантов оплаты:',
    'Онлайн банковской картой — сразу, для заказов с подпиской и разовых',
    'СБП — при получении, только для разовых заказов',
    'Заказ необходимо оплатить до 16.00 по московскому времени за 4 дня до доставки.',
];

const MSK_LIKE_TERMS: TTermsSection[] = [
    { title: DELIVERY_TITLE, rows: MSK_LIKE_DELIVERY_ROWS },
    { title: ADJUSTMENT_TITLE, rows: ADJUSTMENT_MSK_LIKE_ROWS },
    { title: PAYMENT_TITLE, rows: PAYMENT_MSK_LIKE_ROWS },
];
const CITY_TERMS: TTermsSection[] = [
    { title: DELIVERY_TITLE, rows: CITY_DELIVERY_ROWS },
    { title: ADJUSTMENT_TITLE, rows: ADJUSTMENT_SPB_LIKE_ROWS },
    { title: PAYMENT_TITLE, rows: PAYMENT_SPB_LIKE_ROWS },
];

const TERMS_BY_LOCATION: Record<TLocationKey, TTermsSection[]> = {
    MSK: [
        { title: DELIVERY_TITLE, rows: MSK_DELIVERY_ROWS },
        { title: ADJUSTMENT_TITLE, rows: ADJUSTMENT_MSK_LIKE_ROWS },
        { title: PAYMENT_TITLE, rows: PAYMENT_MSK_LIKE_ROWS },
    ],
    SPB: [
        { title: DELIVERY_TITLE, rows: SPB_DELIVERY_ROWS },
        { title: ADJUSTMENT_TITLE, rows: ADJUSTMENT_SPB_LIKE_ROWS },
        { title: PAYMENT_TITLE, rows: PAYMENT_SPB_LIKE_ROWS },
    ],
    NIZ: CITY_TERMS,
    KZN: CITY_TERMS,
    VOR: [
        { title: DELIVERY_TITLE, rows: VOR_DELIVERY_ROWS },
        { title: ADJUSTMENT_TITLE, rows: ADJUSTMENT_SPB_LIKE_ROWS },
        { title: PAYMENT_TITLE, rows: PAYMENT_SPB_LIKE_ROWS },
    ],
    TUL: MSK_LIKE_TERMS,
    YAR: MSK_LIKE_TERMS,
    VLA: MSK_LIKE_TERMS,
    RYA: MSK_LIKE_TERMS,
    KLU: MSK_LIKE_TERMS,
    TVE: MSK_LIKE_TERMS,
};

const getLocationKeyBySubdivision = (countrySubdivision: string): TLocationKey => {
    if (['RU-NIZ', 'RU-NIO'].includes(countrySubdivision)) {
        return 'NIZ';
    }

    if (['RU-SPE', 'RU-LEN'].includes(countrySubdivision)) {
        return 'SPB';
    }

    if (['RU-TA', 'RU-TAO'].includes(countrySubdivision)) {
        return 'KZN';
    }

    if (countrySubdivision === 'RU-TUL') {
        return 'TUL';
    }

    if (countrySubdivision === 'RU-YAR') {
        return 'YAR';
    }

    if (countrySubdivision === 'RU-VLA') {
        return 'VLA';
    }

    if (countrySubdivision === 'RU-VOR') {
        return 'VOR';
    }

    if (countrySubdivision === 'RU-RYA') {
        return 'RYA';
    }

    if (countrySubdivision === 'RU-KLU') {
        return 'KLU';
    }

    if (countrySubdivision === 'RU-TVE') {
        return 'TVE';
    }

    return 'MSK';
};

function DeliveryZoneTermsContent() {
    const { country_subdivision: countrySubdivision } = useContext(LocationContext);

    if (!countrySubdivision) {
        return null;
    }

    const sections = TERMS_BY_LOCATION[getLocationKeyBySubdivision(countrySubdivision)];

    return (
        <div className={css.deliveryZoneTermsRoot}>
            {sections.map((section, index) => {
                const Heading = 'h2';

                return (
                    <div className={css.deliveryZoneTermsSection} key={section.title}>
                        <div className={css.deliveryZoneTermsTitle}>
                            <Heading>{section.title}</Heading>
                        </div>
                        <div className={css.deliveryZoneTermsDescription}>
                            {section.rows.map((row, index) => (
                                <div
                                    className={isHeaderRow(row)
                                        ? `${css.deliveryZoneTermsDescriptionRow} ${css.deliveryZoneTermsDescriptionRowHeader}`
                                        : css.deliveryZoneTermsDescriptionRow}
                                    key={index}
                                >
                                    {row}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function DeliveryZoneTermsDAC() {
    return <DeliveryZoneTermsContent />;
}

export function DeliveryZoneTerms() {
    return <DeliveryZoneTermsDAC />;
}
